// cheatsheet for socket.io options:
//  https://github.com/socketio/socket.io/blob/master/docs/emit.md
import { observable } from 'mobx'
import { guid } from './utils'
import { ICommunicatorUser } from '../../../models'

// let io: Function
// // io reference comes from loading socket.io into the global in the index.html
if (typeof io === 'undefined') {

  // eslint-disable-next-line
  (<any>global).io = () => {
    return {
      on: () => {},
      emit: () => {}
    }
  }
}

export default class Communicator implements CommunicatorInterface {

  @observable isReady = false
  @observable userList = []
  settings
  socket
  user
  createGuid
  queue = []
  logger
  flushingQueue: boolean

  constructor(settings: {url: string}, createGuid: Function = guid) {
    this.settings = settings
    // io reference comes from loading socket.io into the global in the index.html
    this.socket = io(this.settings.url)
    this.user = null

    this.createGuid = createGuid

    this.queue = []
    this.logger = () => {}
  }

  getUserFromList(userId) {
    return this.userList.find(user => user.id === userId)
  }

  setUser(user: ICommunicatorUser) {

    return new Promise<void>((resolve) => {

      // if there is no io instance, just move on
      if (!io) {
        this.isReady = true
        resolve()
        return
      }

      this.user = user

      this.on('ack', () => {
        this.queue.unshift({ action: 'identify' })
        this.flushQueue()
        this.isReady = true
        resolve()
      })

      this.on('connect', () => {
        this.ready({ user })
      })

      this.on('disconnect', () => {
        this.isReady = false
      })

      this.on('message', (comm) => {
        switch (comm.action) {
          case 'listUser':
            this.userList = Object.keys(comm.data).map(id => comm.data[id])
            break
          case 'comment':
            // console.log('message', comm.data.comment, comm.data.userId)

          case 'message':
            // in case it's a chat message:

            break
          default:
            break
        }

      })

      setTimeout(() => {
        this.ready({ user })
      }, 1)

    })

  }

  addLogger(logger) {
    this.logger = logger
  }

  /**
   * @private
   */
  flushQueue() {
    while (this.queue.length) {
      this.forceSend(this.queue.shift())
    }
  }

  send(data) {

    if (!this.isReady || this.flushingQueue) {
      this.queue.push(data)
      return
    }

    this.forceSend(data)

  }

  forceSend(data, cmd = 'message') {

    if (!data) {
      return
    }

    data.user = this.user
    data.mid = this.createGuid()
    this.logger({ action: cmd, ...data }, 'out')

    if (this.socket) {
      this.socket.emit(cmd, data)
    }
  }

  on(evt, cb) {
    if (this.socket) {
      this.socket.on(evt, (data) => {
        this.logger(data || {}, 'in')
        cb(data)
      })
    }
  }

  watch(evt, cb) {
    this.on('message', (data) => {
      if (data.action === evt) {
        cb(data.data)
      }
    })

  }

  /**
   * @private
   */
  ready(data) {
    this.forceSend(data, 'ready')
  }

  /**
   * @private
   */
  setRoom(roomId) {
    this.forceSend(roomId, 'room')
  }

  action(action, data = {}) {
    if (!action) {
      return
    }
    this.send({ action, ...data })
  }

  message(message, recipient) {

    const data = {
      value: message,
      recipient
    }

    this.action('message', data)

  }
}

interface CommunicatorInterface {
  settings: {url: string}
  socket: any
  user: string
  createGuid: Function
  queue: string[]
  logger: Function
}
